import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import {
	DataRetentionSetting,
	getDataRetentionSettings,
	getRetentionPeriodSelectOptions,
	saveChangedDataRetentionSettings,
} from 'core/API/dataRetentionSettings';
import styles from './DataRetentionSettings.module.scss';

interface DataRetentionSettingsProps {
	canEdit: boolean;
	disabled?: boolean;
}

export interface DataRetentionSettingsHandle {
	save: () => Promise<void>;
}

const DataRetentionSettings = forwardRef<DataRetentionSettingsHandle, DataRetentionSettingsProps>(
	function DataRetentionSettings({ canEdit, disabled = false }, ref) {
		const [original, setOriginal] = useState<DataRetentionSetting[]>([]);
		const [draft, setDraft] = useState<DataRetentionSetting[]>([]);
		const [loading, setLoading] = useState(true);
		const [error, setError] = useState<string | null>(null);
		const originalRef = useRef(original);
		const draftRef = useRef(draft);

		originalRef.current = original;
		draftRef.current = draft;

		useEffect(() => {
			let cancelled = false;

			const loadSettings = async () => {
				setLoading(true);
				setError(null);
				try {
					const settings = await getDataRetentionSettings();
					if (!cancelled) {
						setOriginal(settings);
						setDraft(settings.map((setting) => ({ ...setting })));
					}
				} catch (err: any) {
					if (!cancelled) {
						setError(err?.message || 'Failed to load data retention settings');
					}
				} finally {
					if (!cancelled) {
						setLoading(false);
					}
				}
			};

			loadSettings();
			return () => {
				cancelled = true;
			};
		}, []);

		useImperativeHandle(ref, () => ({
			save: async () => {
				await saveChangedDataRetentionSettings(originalRef.current, draftRef.current);
				setOriginal(draftRef.current.map((setting) => ({ ...setting })));
			},
		}));

		const handlePeriodChange = (id: number, retentionPeriod: string) => {
			setDraft((prev) =>
				prev.map((setting) => (setting.id === id ? { ...setting, retentionPeriod } : setting))
			);
		};

		return (
			<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
				<Typography
					fontFamily={'Gotham'}
					color={'rgb(0 0 0 / 60%)'}
					variant="subtitle2"
					sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 2 }}
				>
					Data Retention
				</Typography>

				{error && <div className={styles.error}>{error}</div>}

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '120px' }}>
						<CircularProgress size={20} color="inherit" />
					</Box>
				) : draft.length > 0 ? (
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Module</th>
								<th>Retention Period</th>
							</tr>
						</thead>
						<tbody>
							{draft.map((setting) => (
								<tr key={setting.id}>
									<td>{setting.label}</td>
									<td>
										{canEdit ? (
											<Autocomplete
												options={getRetentionPeriodSelectOptions(setting.retentionPeriod)}
												value={setting.retentionPeriod}
												disableClearable
												disabled={disabled}
												size="small"
												sx={{ width: '100%', maxWidth: 220 }}
												onChange={(_, newValue) => {
													if (newValue) {
														handlePeriodChange(setting.id, newValue);
													}
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														inputProps={{
															...params.inputProps,
															'aria-label': `Retention period for ${setting.label}`,
														}}
													/>
												)}
											/>
										) : (
											setting.retentionPeriod
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				) : !error ? (
					<div className={styles.empty}>No data retention settings found.</div>
				) : null}
			</Box>
		);
	}
);

export default DataRetentionSettings;
